import { useEffect, useRef, useState } from "react";
import { trackEvent } from "@/hooks/useEventTracking";
import { shelterFields } from "@/lib/analyticsEvents";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { ShelterWithDistance } from "@/pages/Index";
import { isUrgentNeedsActive } from "@/types/shelter";
import { Button } from "@/components/ui/button";
import { Locate, Loader2, Maximize2, Minimize2, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { ErrorScreen } from "@/components/ErrorScreen";
import { LOADING_MAP } from "@/lib/uiMessages";

interface ShelterMapProps {
  shelters: ShelterWithDistance[];
  selectedShelterId: string | null;
  onShelterSelect: (shelter: ShelterWithDistance) => void;
  onClearSelection?: () => void;
  userLocation: [number, number] | null;
  onUserLocationChange: (location: [number, number] | null) => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  focusCity?: string;
  onBoundsChange?: (bounds: { north: number; south: number; east: number; west: number } | null) => void;
}

// Fix for default marker icons in leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const createMarkerIcon = (isUrgent: boolean) => {
  return L.divIcon({
    html: `
      <div style="
        width: 26px;
        height: 26px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${isUrgent ? 'hsl(15, 90%, 55%)' : 'hsl(155, 45%, 35%)'};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 3px 8px rgba(0,0,0,0.3);
      ">
        <svg
          style="transform: rotate(45deg); width: 13px; height: 13px; color: white;"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="11" cy="4" r="2"/>
          <circle cx="18" cy="8" r="2"/>
          <circle cx="20" cy="16" r="2"/>
          <path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z"/>
        </svg>
      </div>
    `,
    className: "",
    iconSize: [26, 26],
    iconAnchor: [13, 26],
    popupAnchor: [0, -26],
  });
};

// User location marker
const createUserLocationIcon = () => {
  return L.divIcon({
    html: `
      <div style="
        width: 20px;
        height: 20px;
        background: hsl(210, 100%, 50%);
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.4);
      "></div>
    `,
    className: "",
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

const formatDistance = (distance: number): string => {
  if (distance < 1) {
    return `${Math.round(distance * 1000)} m`;
  }
  return `${distance.toFixed(1)} km`;
};

export const ShelterMap = ({ 
  shelters, 
  selectedShelterId, 
  onShelterSelect,
  onClearSelection,
  userLocation,
  onUserLocationChange,
  isFullscreen = false,
  onToggleFullscreen,
  focusCity,
  onBoundsChange,
}: ShelterMapProps) => {
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const userMarkerRef = useRef<L.Marker | null>(null);
  const lastClickedFromMapRef = useRef(false);
  const [isLocating, setIsLocating] = useState(false);
  const [mapInitError, setMapInitError] = useState<string | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [tilesSlow, setTilesSlow] = useState(false);
  const { hasConsent } = useCookieConsent();

  // Poland center coordinates and bounds
  const defaultCenter: [number, number] = [52.0, 19.0];
  const defaultZoom = 7;
  
  // Poland bounds with padding (SW corner, NE corner)
  const polandBounds: L.LatLngBoundsExpression = [
    [48.5, 13.5], // Southwest (with small padding)
    [55.5, 24.5], // Northeast (with small padding)
  ];

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    try {
      mapRef.current = L.map(mapContainerRef.current, {
        maxBounds: polandBounds,
        maxBoundsViscosity: 1.0,
        minZoom: 6,
        maxZoom: 18,
        zoomControl: false,
      }).setView(defaultCenter, defaultZoom);

      

      const tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      });
      tileLayer.on("load", () => setIsMapReady(true));
      tileLayer.addTo(mapRef.current);

      // Emit bounds whenever map view changes (and once after init)
      const emitBounds = () => {
        if (!mapRef.current || !onBoundsChange) return;
        const b = mapRef.current.getBounds();
        onBoundsChange({
          north: b.getNorth(),
          south: b.getSouth(),
          east: b.getEast(),
          west: b.getWest(),
        });
      };
      mapRef.current.on("moveend", emitBounds);
      mapRef.current.on("zoomend", emitBounds);
      // wstępny emit
      window.setTimeout(emitBounds, 0);

      // Bezpiecznik: jeśli kafelki nie wczytają się w 6s, ukryj overlay i pokaż info
      const slowTimer = window.setTimeout(() => setTilesSlow(true), 6000);
      const readyTimer = window.setTimeout(() => setIsMapReady(true), 12000);
      mapRef.current.once("remove", () => {
        clearTimeout(slowTimer);
        clearTimeout(readyTimer);
      });
    } catch (err) {
      console.error("[ShelterMap] init failed", err);
      setMapInitError(err instanceof Error ? err.message : String(err));
      // Posprzątaj częściowo zainicjalizowaną instancję
      try {
        mapRef.current?.remove();
      } catch {
        // ignoruj
      }
      mapRef.current = null;
      return;
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Listen for shelter-select events from the in-popup clickable area
  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      const shelter = shelters.find(s => s.id === id);
      if (shelter) {
        lastClickedFromMapRef.current = true;
        // Zamknij popup i ukryj tooltip, żeby nie wisiały nad modalem
        mapRef.current?.closePopup();
        markersRef.current.forEach(m => m.closeTooltip());
        onShelterSelect(shelter);
      }
    };
    window.addEventListener('shelter-select', handler);
    return () => window.removeEventListener('shelter-select', handler);
  }, [shelters, onShelterSelect]);

  // Update markers when shelters change
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Filter out shelters without proper address (only city name or empty)
    const sheltersWithAddress = shelters.filter(shelter => {
      if (!shelter.address) return false;
      const addressLower = shelter.address.toLowerCase().trim();
      const cityLower = shelter.city.toLowerCase().trim();
      // Exclude if address is empty, equals city name, or is just city with country
      if (addressLower === '' || addressLower === cityLower) return false;
      if (addressLower === `${cityLower}, polska` || addressLower === `${cityLower}, poland`) return false;
      return true;
    });

    // Add new markers only for shelters with proper addresses
    sheltersWithAddress.forEach((shelter) => {
      const isUrgent = isUrgentNeedsActive(shelter);


      const marker = L.marker([shelter.lat, shelter.lng], {
        icon: createMarkerIcon(isUrgent),
        title: shelter.name,
        alt: `Schronisko: ${shelter.name}`,
      }).addTo(mapRef.current!);
      
      // Add aria-label to marker element for screen readers after it's added to DOM
      marker.on('add', () => {
        const markerElement = marker.getElement();
        if (markerElement) {
          markerElement.setAttribute('aria-label', `Schronisko ${shelter.name}, ${shelter.city}`);
        }
      });
      
      // Also try to set immediately in case 'add' already fired
      const markerElement = marker.getElement();
      if (markerElement) {
        markerElement.setAttribute('aria-label', `Schronisko ${shelter.name}, ${shelter.city}`);
      }

      // Add tooltip for hover
      marker.bindTooltip(shelter.name, {
        permanent: false,
        direction: 'top',
        offset: [0, -30],
        className: 'shelter-tooltip'
      });

      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        lastClickedFromMapRef.current = true;
        marker.closeTooltip();
        trackEvent("Otworzono schronisko", { ...shelterFields(shelter), source: "map" });
        window.dispatchEvent(new CustomEvent('shelter-select', { detail: shelter.id }));
      });
      markersRef.current.push(marker);
    });
  }, [shelters, onShelterSelect]);

  // Sync user marker with userLocation prop and center map
  useEffect(() => {
    if (!mapRef.current) return;

    // Remove previous user marker
    if (userMarkerRef.current) {
      userMarkerRef.current.remove();
      userMarkerRef.current = null;
    }

    // Add new user marker if location exists
    if (userLocation) {
      userMarkerRef.current = L.marker(userLocation, {
        icon: createUserLocationIcon(),
        zIndexOffset: 1000,
        title: 'Twoja lokalizacja',
        alt: 'Twoja lokalizacja',
      }).addTo(mapRef.current);
      
      // Add aria-label to user marker for screen readers
      const userMarkerElement = userMarkerRef.current.getElement();
      if (userMarkerElement) {
        userMarkerElement.setAttribute('aria-label', 'Twoja lokalizacja na mapie');
      }

      userMarkerRef.current.bindPopup(`
        <div style="padding: 8px; text-align: center;">
          <p style="font-weight: 600; margin-bottom: 4px;">📍 Twoja lokalizacja</p>
          <p style="font-size: 12px; color: #666;">Lista posortowana wg odległości</p>
        </div>
      `);

      // Center map on user location with ~30km radius (zoom 10)
      mapRef.current.flyTo(userLocation, 10, { duration: 1 });
    }
  }, [userLocation]);

  // Always-current ref to selectedShelterId (read inside effects without
  // adding it as a dependency — selection changes must NOT retrigger
  // city/filter centering when the user closes the shelter card).
  const selectedShelterIdRef = useRef<string | null>(selectedShelterId);
  useEffect(() => {
    selectedShelterIdRef.current = selectedShelterId;
  }, [selectedShelterId]);

  // Center map on selected shelter — only when triggered from the LIST,
  // not from clicking a marker on the map (handled via lastClickedFromMapRef).
  useEffect(() => {
    if (!mapRef.current || !selectedShelterId) return;

    if (lastClickedFromMapRef.current) {
      lastClickedFromMapRef.current = false;
      return;
    }

    const selectedShelter = shelters.find(s => s.id === selectedShelterId);
    if (selectedShelter) {
      mapRef.current.flyTo([selectedShelter.lat, selectedShelter.lng], 12, { duration: 0.5 });
    }
  }, [selectedShelterId, shelters]);

  // Center on focused city — runs ONLY when the city filter changes.
  const prevFocusCityRef = useRef<string | undefined>(focusCity);
  useEffect(() => {
    if (!mapRef.current) {
      prevFocusCityRef.current = focusCity;
      return;
    }
    const prev = prevFocusCityRef.current;
    prevFocusCityRef.current = focusCity;

    // Only act when the city actually changed.
    if (prev === focusCity) return;
    // Don't fight the shelter-selection or user-location effects.
    if (selectedShelterIdRef.current) return;
    if (userLocation) return;

    if (!focusCity) {
      // City was cleared — reset to default Poland view.
      mapRef.current.flyTo(defaultCenter, defaultZoom, { duration: 0.5 });
      return;
    }

    const cityShelters = shelters.filter(s => s.city === focusCity);
    if (cityShelters.length === 0) return;

    if (cityShelters.length === 1) {
      mapRef.current.flyTo([cityShelters[0].lat, cityShelters[0].lng], 12, { duration: 0.5 });
    } else {
      const bounds = L.latLngBounds(cityShelters.map(s => [s.lat, s.lng] as [number, number]));
      mapRef.current.flyToBounds(bounds, { padding: [50, 50], duration: 0.5 });
    }
  }, [focusCity, shelters, userLocation]);

  // Auto-fit when the filtered shelters list itself changes to a small set
  // (e.g. via search). Runs only when the SHAPE of the shelters list changes,
  // never on selection toggles.
  const prevSheltersKeyRef = useRef<string>("");
  useEffect(() => {
    if (!mapRef.current) return;
    if (selectedShelterIdRef.current) return;
    if (userLocation) return;
    if (focusCity) return; // city effect owns this case

    const key = shelters.map(s => s.id).join(",");
    if (key === prevSheltersKeyRef.current) return;
    prevSheltersKeyRef.current = key;

    if (shelters.length > 0 && shelters.length <= 5) {
      if (shelters.length === 1) {
        mapRef.current.flyTo([shelters[0].lat, shelters[0].lng], 12, { duration: 0.5 });
      } else {
        const bounds = L.latLngBounds(shelters.map(s => [s.lat, s.lng] as [number, number]));
        mapRef.current.flyToBounds(bounds, { padding: [50, 50], duration: 0.5 });
      }
    }
  }, [shelters, focusCity, userLocation]);




  // Handle geolocation
  const handleLocateUser = () => {
    if (!hasConsent) {
      toast.error("Musisz zaakceptować cookies, aby korzystać z geolokalizacji");
      return;
    }

    if (!navigator.geolocation) {
      toast.error("Twoja przeglądarka nie obsługuje geolokalizacji");
      return;
    }

    setIsLocating(true);

    // Detect iOS/iPadOS for specific error messages
    const ua = navigator.userAgent;
    const isIOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (ua.includes("Macintosh") && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1);
    
    const showGeolocationError = (error: GeolocationPositionError) => {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          // Show a shorter toast for the map button - the main detailed popup is in Index.tsx
          toast.error(
            isIOS 
              ? "Lokalizacja zablokowana. Użyj przycisku lokalizacji na liście schronisk, aby zobaczyć instrukcje."
              : "Brak dostępu do lokalizacji. Włącz w ustawieniach przeglądarki i odśwież.",
            { duration: 6000 }
          );
          break;
        case error.POSITION_UNAVAILABLE:
          if (isIOS) {
            toast.error("Nie udało się określić lokalizacji. Sprawdź czy Usługi lokalizacji są włączone: Ustawienia → Prywatność → Usługi lokalizacji.", {
              duration: 10000,
            });
          } else {
            toast.error("Nie udało się określić lokalizacji. Sprawdź czy GPS/lokalizacja jest włączona.");
          }
          break;
        case error.TIMEOUT:
          toast.error("Upłynął czas oczekiwania na lokalizację. Upewnij się, że masz włączoną lokalizację i spróbuj ponownie.");
          break;
        default:
          toast.error("Błąd podczas pobierania lokalizacji");
      }
    };

    // iOS Safari requires specific handling - try with high accuracy first, then fallback
    const handleSuccess = (position: GeolocationPosition) => {
      const { latitude, longitude } = position.coords;
      
      // Check if location is in Poland bounds
      if (latitude < 48.5 || latitude > 55.5 || longitude < 13.5 || longitude > 24.5) {
        toast.error("Twoja lokalizacja jest poza granicami Polski");
        setIsLocating(false);
        return;
      }

      // Clear any selected shelter so the map doesn't try to re-center on it
      if (onClearSelection) {
        onClearSelection();
      }

      onUserLocationChange([latitude, longitude]);

      // Fly to user location with zoom level 10 (~30km radius visible)
      if (mapRef.current) {
        mapRef.current.flyTo([latitude, longitude], 10, { duration: 1.5 });
      }
      
      toast.success("Mapa wyśrodkowana na Twojej lokalizacji!");
      setIsLocating(false);
    };

    const handleError = (error: GeolocationPositionError) => {
      // On iOS, if high accuracy fails, try without it
      if (error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE) {
        navigator.geolocation.getCurrentPosition(
          handleSuccess,
          (fallbackError) => {
            setIsLocating(false);
            showGeolocationError(fallbackError);
          },
          {
            enableHighAccuracy: false,
            timeout: 15000,
            maximumAge: 600000, // 10 minutes cache
          }
        );
        return;
      }
      
      setIsLocating(false);
      showGeolocationError(error);
    };

    navigator.geolocation.getCurrentPosition(
      handleSuccess,
      handleError,
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes cache
      }
    );
  };


  // Invalidate map size when fullscreen changes
  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current?.invalidateSize();
      }, 100);
    }
  }, [isFullscreen]);

  return (
    <div className="relative h-full w-full">
      <div
        ref={mapContainerRef}
        className="h-full w-full rounded-xl overflow-hidden shadow-elevated"
      />

      {!mapInitError && !isMapReady && (
        <div
          role="status"
          aria-live="polite"
          className="absolute inset-0 z-[1050] bg-background/80 backdrop-blur-sm rounded-xl flex flex-col items-center justify-center gap-3 pointer-events-none"
        >
          <div
            className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin"
            aria-hidden="true"
          />
          <p className="text-sm text-foreground font-medium">{LOADING_MAP}</p>
          {tilesSlow && (
            <p className="text-xs text-muted-foreground max-w-[240px] text-center px-4">
              To trwa dłużej niż zwykle. Sprawdź połączenie z internetem.
            </p>
          )}
        </div>
      )}

      {mapInitError && (
        <div className="absolute inset-0 z-[1100] bg-background/95 backdrop-blur-sm rounded-xl overflow-hidden">
          <ErrorScreen
            fullScreen={false}
            title="Nie udało się załadować mapy"
            description="Mapa nie zainicjalizowała się poprawnie. Sprawdź połączenie i odśwież stronę. Lista schronisk po prawej stronie nadal działa."
            details={mapInitError}
          />
        </div>
      )}

      {/* Control buttons */}
      <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2.5">
        {onToggleFullscreen && (
          <Button
            onClick={onToggleFullscreen}
            size="icon"
            variant="secondary"
            className="h-7 w-7 sm:h-8 sm:w-8 rounded-xl bg-white text-gray-700 border-0 shadow-card hover:bg-gray-50 hover:shadow-elevated transition-shadow"
            title={isFullscreen ? "Zamknij pełny ekran" : "Powiększ mapę"}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </Button>
        )}
        <Button
          onClick={handleLocateUser}
          disabled={isLocating}
          size="icon"
          variant="secondary"
          className="h-7 w-7 sm:h-8 sm:w-8 rounded-xl bg-white text-gray-700 border-0 shadow-card hover:bg-gray-50 hover:shadow-elevated transition-shadow"
          title="Znajdź moją lokalizację"
        >
          {isLocating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Locate className="w-4 h-4" />
          )}
        </Button>
        <div className="flex flex-col rounded-xl overflow-hidden bg-white shadow-card">
          <Button
            onClick={() => mapRef.current?.zoomIn()}
            size="icon"
            variant="secondary"
            className="h-7 w-7 sm:h-8 sm:w-8 rounded-none bg-white text-gray-700 border-0 border-b border-gray-200 hover:bg-gray-50 shadow-none"
            title="Przybliż"
            aria-label="Przybliż"
          >
            <Plus className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => mapRef.current?.zoomOut()}
            size="icon"
            variant="secondary"
            className="h-7 w-7 sm:h-8 sm:w-8 rounded-none bg-white text-gray-700 border-0 hover:bg-gray-50 shadow-none"
            title="Oddal"
            aria-label="Oddal"
          >
            <Minus className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
